import { Percent } from '@kodiak-finance/sdk-core'
import { ButtonSecondary } from 'components/Button'
import { ReactNode, useCallback, useState } from 'react'
import { RefreshCw } from 'react-feather'
import { LiquidityProvider } from 'state/providers/reducer'
import styled from 'styled-components'

import SettingsTab from '../Settings'

const StyledSwapHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  color: ${({ theme }) => theme.textPrimary};
`

const Title = styled.h1`
  font-size: 1.875rem;
  font-weight: 700;
  color: ${({ theme }) => theme.textPrimary};
  margin: 0;
`

const ButtonsContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
`

const RefreshButton = styled(ButtonSecondary)<{ isRefreshing?: boolean }>`
  width: 40px;
  height: 40px;
  border-radius: 4px !important;
  background-color: rgba(71, 85, 105, 0.5) !important;
  border: none !important;

  &:hover {
    background-color: rgba(71, 85, 105, 0.7) !important;
  }

  svg {
    width: 16px;
    height: 16px;
    color: ${({ theme }) => theme.textPrimary} !important;
    ${({ isRefreshing }) =>
      isRefreshing &&
      `
      animation: spin 1s linear infinite;
    `}
  }

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`

const StyledSettingsWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  .settings-button,
  button {
    width: 40px !important;
    height: 40px !important;
    border-radius: 4px !important;
    background-color: rgba(71, 85, 105, 0.5) !important;
    border: none !important;

    &:hover {
      background-color: rgba(71, 85, 105, 0.7) !important;
    }

    /* Target the nested structure: button > div > svg */
    svg {
      width: 20px !important;
      height: 20px !important;
    }
  }
`

export interface ProvidersConfigProps {
  isLoading: boolean
  availableProviders: LiquidityProvider[]
  selectedProviders: string[]
  onSelect: (providerId: string) => void
  onReset: () => void
  onSelectAll: () => void
}

interface SwapHeaderProps {
  allowedSlippage: Percent
  onRefresh?: () => void
  providersConfig?: ProvidersConfigProps
  buttons?: ReactNode
  title?: string
  isTwap: boolean
}

export default function SwapHeader({
  allowedSlippage,
  onRefresh,
  providersConfig,
  buttons,
  title = 'Swap',
  isTwap,
}: SwapHeaderProps) {
  const [isRefreshing, setIsRefreshing] = useState(false)

  const handleRefresh = useCallback(() => {
    setIsRefreshing(true)
    onRefresh?.()

    const timeoutId = setTimeout(() => setIsRefreshing(false), 1000)
    return () => clearTimeout(timeoutId)
  }, [onRefresh])

  return (
    <StyledSwapHeader>
      <Title>{title}</Title>
      <ButtonsContainer>
        {onRefresh && (
          <RefreshButton onClick={handleRefresh} isRefreshing={isRefreshing} aria-label="Refresh quotes">
            <RefreshCw />
          </RefreshButton>
        )}
        {providersConfig && (
          <StyledSettingsWrapper>
            <SettingsTab
              isTwap={isTwap}
              placeholderSlippage={allowedSlippage}
              providersConfig={{
                isLoading: providersConfig.isLoading,
                availableProviders: providersConfig.availableProviders,
                selectedProviders: providersConfig.selectedProviders,
                onSelect: providersConfig.onSelect,
                onReset: providersConfig.onReset,
                onSelectAll: providersConfig.onSelectAll,
              }}
            />
          </StyledSettingsWrapper>
        )}
        {buttons}
      </ButtonsContainer>
    </StyledSwapHeader>
  )
}
